import axios from 'axios'
import type { AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import type {
  ApiResponse,
  Tenant,
  CreateTenantRequest,
  UpdateTenantRequest,
  TenantFilters,
  PaginationParams,
  PaginatedResponse,
  DashboardStats,
  RecentActivity,
  SystemHealth,
  AnalyticsData,
  BillingStats,
  CreateInvoiceRequest,
  Invoice,
  InvoicesResponse,
  InvoiceFilters,
  TenantCreateResponse
} from '@/types'
import { toast } from 'vue3-toastify'

// API Configuration
// Utiliser une URL relative pour passer par le proxy nginx
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

class ApiService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: false, // Désactivé car le backend utilise Access-Control-Allow-Origin: *
      // Adapter automatiquement le protocole si nécessaire
      adapter: axios.defaults.adapter,
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = localStorage.getItem('super_admin_token')
        console.log('Super Admin Token:', token ? 'Present' : 'Missing')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        } else {
          console.warn('No super_admin_token found in localStorage')
        }

        // Add request timestamp for debugging
        config.metadata = { startTime: new Date() }

        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log response time in development
        if (import.meta.env.DEV && response.config.metadata) {
          const duration = new Date().getTime() - response.config.metadata.startTime.getTime()
          console.log(`API ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`)
        }

        return response
      },
      (error: AxiosError) => {
        this.handleApiError(error)
        return Promise.reject(error)
      }
    )
  }

  private handleApiError(error: AxiosError) {
    console.error('API Error:', error)

    if (error.response) {
      const status = error.response.status
      let message = (error.response.data as any)?.detail || error.message

      // Traduire le message d'erreur backend en français
      if (typeof message === 'string') {
        message = this.translateErrorMessage(message)
      }

      switch (status) {
        case 401:
          // Ne pas déclencher handleUnauthorized pour les requêtes de login
          const isLoginRequest = error.config?.url?.includes('/auth/login')
          if (!isLoginRequest) {
            this.handleUnauthorized()
          }
          break
        case 403:
          toast.error('Accès interdit. Permissions insuffisantes.')
          break
        case 404:
          toast.error('Ressource non trouvée.')
          break
        case 422:
          // Validation errors are handled by components
          break
        case 429:
          toast.error('Trop de requêtes. Veuillez patienter.')
          break
        case 500:
          toast.error('Erreur serveur. Veuillez réessayer plus tard.')
          break
        default:
          toast.error(message || 'Une erreur inattendue s\'est produite.')
      }
    } else if (error.request) {
      // Erreur réseau ou CORS
      console.error('Network error:', error.request)
      if (error.message?.includes('CORS') || error.message?.includes('Cross-Origin')) {
        toast.error('Erreur CORS: Impossible de joindre l\'API. Vérifiez la configuration.')
      } else {
        toast.error('Impossible de joindre le serveur. Vérifiez votre connexion.')
      }
    } else {
      toast.error('Erreur de configuration de la requête.')
    }
  }

  private handleUnauthorized() {
    console.error('🚨 UNAUTHORIZED ERROR - Token supprimé')
    console.error('URL actuelle:', window.location.href)
    console.error('Stack trace:', new Error().stack)
    
    localStorage.removeItem('super_admin_token')
    localStorage.removeItem('super_admin_user')
    
    toast.error('Session expirée. Veuillez vous reconnecter.')
    
    // Redirection vers la page de login avec la base URL correcte
    const loginPath = '/geopresence/super-admin/login'
    if (window.location.pathname !== loginPath) {
      window.location.href = loginPath
    }
  }

  // Generic request methods
  async get<T = any>(url: string, params?: any): Promise<T> {
    const response = await this.api.get<ApiResponse<T>>(url, { params })
    console.log('API Response for', url, ':', response.data)
    
    // Si la réponse a déjà la structure attendue, la retourner directement
    if (response.data && typeof response.data === 'object' && !response.data.data) {
      return response.data as T
    }
    
    return response.data.data
  }

  async post<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.api.post<ApiResponse<T>>(url, data)
    console.log('API POST Response for', url, ':', response.data)
    
    // Si la réponse a déjà la structure attendue, la retourner directement
    if (response.data && typeof response.data === 'object' && !response.data.data) {
      return response.data as T
    }
    
    return response.data.data
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.api.put<ApiResponse<T>>(url, data)
    console.log('API PUT Response for', url, ':', response.data)
    
    // Si la réponse a déjà la structure attendue, la retourner directement
    if (response.data && typeof response.data === 'object' && !response.data.data) {
      return response.data as T
    }
    
    return response.data.data
  }

  async patch<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.api.patch<ApiResponse<T>>(url, data)
    console.log('API PATCH Response for', url, ':', response.data)
    
    // Si la réponse a déjà la structure attendue, la retourner directement
    if (response.data && typeof response.data === 'object' && !response.data.data) {
      return response.data as T
    }
    
    return response.data.data
  }

  async delete<T = any>(url: string): Promise<T> {
    const response = await this.api.delete<ApiResponse<T>>(url)
    console.log('API DELETE Response for', url, ':', response.data)
    
    // Si la réponse a déjà la structure attendue, la retourner directement
    if (response.data && typeof response.data === 'object' && !response.data.data) {
      return response.data as T
    }
    
    return response.data.data
  }

  // Tenant Management
  async getTenants(
    filters?: TenantFilters,
    pagination?: PaginationParams
  ): Promise<PaginatedResponse<Tenant>> {
    // Par défaut, ne charger que les tenants actifs
    const params = { 
      is_active: true, // Filtre par défaut pour les tenants actifs
      ...filters, 
      ...pagination 
    }
    return this.get('/super-admin/tenants', params)
  }

  async getTenant(tenantId: number): Promise<Tenant> {
    return this.get(`/super-admin/tenants/${tenantId}`)
  }

  async createTenant(data: CreateTenantRequest): Promise<TenantCreateResponse> {
    return this.post('/super-admin/tenants', data)
  }

  async updateTenant(tenantId: number, data: UpdateTenantRequest): Promise<Tenant> {
    return this.put(`/super-admin/tenants/${tenantId}`, data)
  }

  async suspendTenant(tenantId: number): Promise<{ message: string }> {
    return this.post(`/super-admin/tenants/${tenantId}/suspend`)
  }

  async activateTenant(tenantId: number): Promise<{ message: string }> {
    return this.post(`/super-admin/tenants/${tenantId}/activate`)
  }

  async deleteTenant(tenantId: number): Promise<{ message: string }> {
    return this.delete(`/super-admin/tenants/${tenantId}`)
  }

  // Dashboard and Analytics
  async getDashboardStats(): Promise<DashboardStats> {
    return this.get('/super-admin/dashboard/stats')
  }

  async getRecentActivity(limit = 10): Promise<RecentActivity[]> {
    return this.get('/super-admin/dashboard/activity', { limit })
  }

  async getSystemHealth(): Promise<SystemHealth> {
    return this.get('/super-admin/system/health')
  }

  async getAnalytics(
    period: 'day' | 'week' | 'month' | 'year',
    startDate?: string,
    endDate?: string
  ): Promise<AnalyticsData> {
    const params = { period, start_date: startDate, end_date: endDate }
    return this.get('/super-admin/analytics', params)
  }

  // Authentication
  async login(email: string, password: string): Promise<{ access_token: string; user_id: number; role: string }> {
    const response = await this.api.post('/auth/login', { email, password })
    return response.data
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/super-admin/auth/logout')
    } finally {
      localStorage.removeItem('super_admin_token')
      localStorage.removeItem('super_admin_user')
    }
  }

  async refreshToken(): Promise<{ token: string }> {
    const response = await this.api.post('/super-admin/auth/refresh')
    return response.data
  }

  async getCurrentUser(): Promise<any> {
    return this.get('/super-admin/auth/me')
  }

  // File Upload
  async uploadFile(file: File, onProgress?: (progress: number) => void): Promise<{ url: string }> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })

    return response.data
  }

  // Billing Management
  async getBillingStats(period?: string): Promise<BillingStats> {
    const params = period ? { period } : {}
    return this.get('/super-admin/billing/stats', params)
  }

  async createInvoice(data: CreateInvoiceRequest): Promise<Invoice> {
    return this.post('/super-admin/billing/invoices', data)
  }

  async getInvoices(filters?: InvoiceFilters): Promise<InvoicesResponse> {
    console.log('🚀 Calling getInvoices with filters:', filters)
    const result = await this.get('/super-admin/billing/invoices', filters)
    console.log('📦 Raw API response for invoices:', result)
    return result
  }

  async getInvoice(invoiceId: number): Promise<any> {
    return this.get(`/super-admin/billing/invoices/${invoiceId}`)
  }

  async generateInvoices(): Promise<{ message: string; invoices_created: number }> {
    return this.post('/super-admin/billing/invoices/generate-monthly', {})
  }

  async sendInvoice(invoiceId: number): Promise<{ message: string }> {
    return this.post(`/super-admin/billing/invoices/${invoiceId}/send`)
  }

  async downloadInvoice(invoiceId: number): Promise<Blob> {
    const response = await this.api.get(`/super-admin/billing/invoices/${invoiceId}/pdf`, {
      responseType: 'blob'
    })
    return response.data
  }

  async getFailedPayments(): Promise<any[]> {
    return this.get('/super-admin/billing/failed-payments')
  }

  async retryPayment(paymentId: number): Promise<{ message: string }> {
    return this.post(`/super-admin/billing/payments/${paymentId}/retry`)
  }

  async getUpcomingRenewals(days = 30): Promise<any[]> {
    return this.get('/super-admin/billing/renewals', { days })
  }

  async getRevenueBreakdown(period?: string): Promise<any[]> {
    const params = period ? { period } : {}
    return this.get('/super-admin/billing/revenue-breakdown', params)
  }

  async getPaymentMethods(): Promise<any[]> {
    return this.get('/super-admin/billing/payment-methods')
  }

  // Export Data
  async exportTenants(format: 'csv' | 'xlsx' | 'json' = 'csv'): Promise<Blob> {
    const response = await this.api.get('/super-admin/tenants/export', {
      params: { format },
      responseType: 'blob',
    })
    return response.data
  }

  async exportAnalytics(
    period: string,
    format: 'csv' | 'xlsx' | 'json' = 'csv'
  ): Promise<Blob> {
    const response = await this.api.get('/super-admin/analytics/export', {
      params: { period, format },
      responseType: 'blob',
    })
    return response.data
  }

  async exportInvoices(
    filters?: any,
    format: 'csv' | 'xlsx' | 'json' = 'csv'
  ): Promise<Blob> {
    const response = await this.api.get('/super-admin/billing/invoices/export', {
      params: { ...filters, format },
      responseType: 'blob',
    })
    return response.data
  }

  // Utility methods
  async testConnection(): Promise<boolean> {
    try {
      console.log('Testing connection to:', API_BASE_URL)
      const response = await this.api.get('/health')
      console.log('Connection test successful:', response)
      return true
    } catch (error) {
      console.error('Connection test failed:', error)
      return false
    }
  }

  async validateTenantSlug(slug: string): Promise<{ available: boolean; suggestions?: string[] }> {
    return this.get('/tenant/validate-slug', { slug })
  }

  // Error handling utility
  extractValidationErrors(error: AxiosError): Record<string, string[]> {
    if (error.response?.status === 422 && error.response.data) {
      const data = error.response.data as any
      return data.detail || data.errors || {}
    }
    return {}
  }

  // Traduire les messages d'erreur backend en français
  translateErrorMessage(message: string): string {
    const translations: Record<string, string> = {
      // Erreurs de validation courantes
      'This field is required.': 'Ce champ est obligatoire.',
      'This field may not be blank.': 'Ce champ ne peut pas être vide.',
      'Enter a valid email address.': 'Veuillez entrer une adresse email valide.',
      'A user with this email already exists.': 'Un utilisateur avec cet email existe déjà.',
      'A tenant with this slug already exists.': 'Une entreprise avec cet identifiant existe déjà.',
      'A tenant with this email already exists.': 'Une entreprise avec cet email existe déjà.',
      'Invalid password.': 'Mot de passe invalide.',
      'Password must be at least 8 characters.': 'Le mot de passe doit contenir au moins 8 caractères.',
      'The two password fields didn\'t match.': 'Les mots de passe ne correspondent pas.',
      'Invalid credentials.': 'Identifiants invalides.',
      'User account is disabled.': 'Le compte utilisateur est désactivé.',
      'Unable to log in with provided credentials.': 'Impossible de se connecter avec les identifiants fournis.',
      'This password is too short. It must contain at least 8 characters.': 'Ce mot de passe est trop court. Il doit contenir au moins 8 caractères.',
      'This password is too common.': 'Ce mot de passe est trop courant.',
      'This password is entirely numeric.': 'Ce mot de passe est entièrement numérique.',
      'Email already registered.': 'Email déjà enregistré.',
      'Slug already exists.': 'Cet identifiant existe déjà.',
      'Invalid slug format.': 'Format d\'identifiant invalide.',
      'Tenant not found.': 'Entreprise non trouvée.',
      'User not found.': 'Utilisateur non trouvé.',
      'Invalid token.': 'Jeton invalide.',
      'Token has expired.': 'Le jeton a expiré.',
      'Permission denied.': 'Permission refusée.',
      'Access denied.': 'Accès refusé.',
      'Invalid request.': 'Requête invalide.',
      'Method not allowed.': 'Méthode non autorisée.',
      'Server error.': 'Erreur serveur.',
      'Network error.': 'Erreur réseau.',
      'Connection failed.': 'Connexion échouée.',
      'Request timeout.': 'Délai d\'attente de la requête dépassé.',
      'Too many requests.': 'Trop de requêtes.',
      'Rate limit exceeded.': 'Limite de taux dépassée.',
      'Invalid file format.': 'Format de fichier invalide.',
      'File too large.': 'Fichier trop volumineux.',
      'Invalid data format.': 'Format de données invalide.',
      'Field required': 'Champ obligatoire',
      'Invalid input': 'Saisie invalide',
      'value is not a valid integer': 'La valeur n\'est pas un entier valide',
      'value is not a valid number': 'La valeur n\'est pas un nombre valide',
      'ensure this value has at least 8 characters': 'assurez-vous que cette valeur contient au moins 8 caractères',
      'This field cannot be blank': 'Ce champ ne peut pas être vide',
      'field is required': 'champ est obligatoire',
    }

    // Chercher une traduction exacte
    if (translations[message]) {
      return translations[message]
    }

    // Chercher une traduction partielle (pour les messages qui contiennent des parties traduisibles)
    for (const [english, french] of Object.entries(translations)) {
      if (message.includes(english)) {
        return message.replace(english, french)
      }
    }

    // Si pas de traduction trouvée, retourner le message original
    return message
  }
}

// Create singleton instance
export const apiService = new ApiService()

// Export types for convenience
export type { ApiResponse, PaginatedResponse }

// Export useful utilities
export const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export const formatCurrency = (amount: number, currency = 'XOF', locale = 'fr-FR') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount)
}

export const formatNumber = (number: number, locale = 'fr-FR') => {
  return new Intl.NumberFormat(locale).format(number)
}

export const formatApiError = (error: AxiosError): string => {
  if (error.response?.data) {
    const data = error.response.data as any
    return data.detail || data.message || 'Une erreur s\'est produite'
  }
  return error.message || 'Erreur de connexion'
}

export default apiService